import React, { useState } from "react";
import RevealAnimation from "../RevealAnimation";
import "./mmpRevolutionSection.scss";
import {
  FiCheck,
  FiX,
  FiAlertTriangle,
  FiArrowRight,
  FiBox,
  FiLayers,
  FiTrendingUp,
  FiShield,
  FiActivity,
  FiDollarSign,
  FiCpu,
} from "react-icons/fi";
import { navigate } from "gatsby";

const MMPRevolutionSection = () => {
  const [hoveredRow, setHoveredRow] = useState(null);

  const features = [
    { name: "Must-Have", mvp: true, mmp: true, icon: <FiBox /> },
    { name: "Should-Have", mvp: false, mmp: true, icon: <FiLayers /> },
    { name: "Could-Have", mvp: false, mmp: true, icon: <FiLayers /> },
    { name: "Growth Loops", mvp: false, mmp: true, icon: <FiTrendingUp /> },
    { name: "Secure by Design", mvp: false, mmp: true, icon: <FiShield /> },
    {
      name: "Analytics by Design",
      mvp: false,
      mmp: true,
      icon: <FiActivity />,
    },
    {
      name: "Investor-Ready",
      mvp: "weak",
      mmp: "strong",
      icon: <FiDollarSign />,
    },
    { name: "Marketability", mvp: "low", mmp: "high", icon: <FiCpu /> },
  ];

  const renderStatus = (status, isMmp) => {
    if (status === true)
      return (
        <div className={`status-icon check ${isMmp ? "mmp-check" : ""}`}>
          <FiCheck />
        </div>
      );
    if (status === false)
      return (
        <div className="status-icon cross">
          <FiX />
        </div>
      );
    if (status === "weak")
      return (
        <span className="status-text weak">
          <FiAlertTriangle /> Weak
        </span>
      );
    if (status === "strong")
      return (
        <span className="status-text strong">
          <FiCheck /> Strong
        </span>
      );
    if (status === "low") return <span className="status-text low">Low</span>;
    if (status === "high")
      return <span className="status-text high">High</span>;
    return status;
  };

  return (
    <section className="mmp-revolution-section" id="mmp-revolution">
      <div className="container">
        <div className="content-wrapper">
          <RevealAnimation>
            <div className="section-header">
              <div className="badge-container">
                <span className="section-badge">Product Strategy</span>
              </div>
              <h2>The MMP Revolution</h2>
              <p className="subtitle">Why MVP Is Dead</p>
              <p className="description">
                The market no longer rewards MVPs. Today, you need a{" "}
                <strong>Minimally Marketable Product (MMP)</strong> - something
                users want, investors back, and markets validate.
              </p>
            </div>
          </RevealAnimation>

          <div className="mmp-visual-container">
            <div className="visual-card mvp-card">
              <div className="card-spine"></div>
              <div className="card-content">
                <div className="card-icon">
                  <FiBox />
                </div>
                <h3>MVP</h3>
                <p>Minimum Viable Product</p>
                <div className="status-bar">
                  <div className="progress-card" style={{ width: "30%" }}></div>
                </div>
                <span className="label">Functional but Basic</span>
              </div>
            </div>

            <div className="visual-arrow">
              <div className="arrow-line"></div>
              <div className="arrow-head"></div>
            </div>

            <div className="visual-card mmp-card">
              <div className="card-spine"></div>
              <div className="card-content">
                <div className="badge">New Standard</div>
                <div className="card-icon">
                  <FiCpu />
                </div>
                <h3>MMP</h3>
                <p>Minimally Marketable Product</p>
                <div className="status-bar">
                  <div
                    className="progress-card"
                    style={{ width: "100%" }}
                  ></div>
                </div>
                <span className="label">Scalable & Investor Ready</span>
              </div>
            </div>
          </div>

          <div className="mmp-bento-grid">
            <div className="bento-item table-item">
              <div className="mmp-comparison-container">
                <div className="comparison-header">
                  <div className="col feature-col">Feature</div>
                  <div className="col mvp-col">MVP</div>
                  <div className="col mmp-col">MMP</div>
                </div>

                <div className="comparison-body">
                  {features.map((feature, index) => (
                    <RevealAnimation key={index} delay={index * 0.05}>
                      <div
                        className={`comparison-row ${
                          hoveredRow === index ? "hovered" : ""
                        }`}
                        onMouseEnter={() => setHoveredRow(index)}
                        onMouseLeave={() => setHoveredRow(null)}
                      >
                        <div className="col feature-col">
                          <span className="feature-icon">{feature.icon}</span>
                          {feature.name}
                        </div>
                        <div className="col mvp-col">
                          {renderStatus(feature.mvp, false)}
                        </div>
                        <div className="col mmp-col">
                          {renderStatus(feature.mmp, true)}
                        </div>
                      </div>
                    </RevealAnimation>
                  ))}
                </div>
              </div>
            </div>

            <div className="bento-column">
              <RevealAnimation delay={0.4}>
                <div className="bento-item timeline-item">
                  <div className="mmp-timeline-highlight">
                    <div className="highlight-content">
                      <h3>With Digiryte:</h3>
                      <div className="equation">
                        <div className="term-box mmp-term">MMP</div>
                        <span className="equals">=</span>
                        <div className="term-box mvp-term">MVP Timeline</div>
                      </div>
                      <p>
                        Thanks to AI-assisted development, Powerhouse team, and
                        reusable accelerators.
                      </p>
                    </div>
                  </div>
                </div>
              </RevealAnimation>

              <RevealAnimation delay={0.6}>
                <div className="bento-item cta-item">
                  <div className="cta-container">
                    <h3>Ready to Build?</h3>
                    <p>Start your MMP journey today.</p>
                    <button
                      className="btn-primary"
                      onClick={() => {
                        navigate("/contact-us");
                      }}
                    >
                      See How MMP Works <FiArrowRight />
                    </button>
                  </div>
                </div>
              </RevealAnimation>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MMPRevolutionSection;
